import { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import Input from "@/components/common/inputs/Input";
import ProfilePicker from "@/components/specific/Profile_Picker";
import CountryFlag from "@/components/common/Country_Flag";
import WebIcon from "@/components/common/Web_Icons";
import { useLoader } from "@/hooks/LoaderProvider";
import Error from "@/components/common/alerts/Error";
import Success from "@/components/common/alerts/Success";
import { logout } from "@/store/redux/slices/auth/auth_slice";
import {
  updateProfileAsync,
  selectIsSearching,
  selectUpdateError,
  selectUserProfile,
} from "@/store/redux/slices/user/user_slice";
import { uploadFile } from "@/store/redux/slices/user/file_upload_slice";
import { clearAccessToken } from "@/utils/token_functions";

const ManageAccount = forwardRef(
  ({ profileImage = null, onProfileImageChange }, ref) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showLoader, hideLoader } = useLoader();

    const isSearching = useSelector(selectIsSearching);
    const updateError = useSelector(selectUpdateError);
    const userProfile = useSelector(selectUserProfile);
    const [shouldRedirectToSignin, setShouldRedirectToSignin] = useState(false);

    const [formData, setFormData] = useState({
      fullname: "",
      phone: "",
      email: "",
    });
    const [isErrorVisible, setIsErrorVisible] = useState(false);
    const [isSuccessVisible, setIsSuccessVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [currentProfileImage, setCurrentProfileImage] = useState(null);

    useEffect(() => {
      if (userProfile) {
        setFormData({
          fullname: userProfile.fullname || "",
          phone: userProfile.phone || "",
          email: userProfile.email || "",
          // Removed _id
        });

        if (userProfile.profileImage) {
          setCurrentProfileImage(userProfile.profileImage);
        }
      }
    }, [userProfile]);


    // Show error message when update fails
    useEffect(() => {
      if (updateError) {
        setIsErrorVisible(true);
      }
    }, [updateError]);

    const handleChange = (e, field) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

    // Expose handleSubmit to parent
    useImperativeHandle(ref, () => ({
      handleSubmit: async () => {
        showLoader();
        try {
            // Capture the email BEFORE we update, so we can reliably detect
            // whether the user changed it even after Redux userProfile refreshes.
            const previousEmail = (userProfile?.email || "").trim().toLowerCase();

          let profileImageUrl = null;

          if (selectedFile) {
            const uploadResult = await dispatch(
              uploadFile({ file: selectedFile })
            ).unwrap();
            if (uploadResult) {
              profileImageUrl = uploadResult.fileUrl;
            }
          }

          const updateData = {
            ...formData,
            ...(profileImageUrl && { profileImage: profileImageUrl }),
          };

          // Using the new updateProfileAsync thunk
          await dispatch(updateProfileAsync(updateData)).unwrap();

          const updatedEmail = (updateData.email || "").trim().toLowerCase();
          const emailChanged =
            previousEmail && updatedEmail && previousEmail !== updatedEmail;

          // If email changed, we DO NOT redirect immediately.
          // We keep the success modal visible, and redirect only after the user
          // clicks the modal's "Continue" button.
          setShouldRedirectToSignin(Boolean(emailChanged));

          setIsSuccessVisible(true);
          setSelectedFile(null);
        } catch (error) {
          console.error("Update failed:", error);
          setErrorMessage({
            header: "Update Failed",
            paragraph: "Failed to update user information. Please try again.",
          });
          setIsErrorVisible(true);
        } finally {
          hideLoader();
        }
      },
    }));

    return (
      <>
        <div className="mt-4">
          <h2 className="mb-2 text-bold">Personal Information</h2>
          <p className="mb-4 text-regular text-lighterGrey">
            This can be your basic profile picture to serve as your unique
            identifier in case of mismatch in the future. You can also change or
            modify your display Full Name below.
          </p>

          <ProfilePicker
            currentImage={currentProfileImage || profileImage}
            onImageChange={(file, previewUrl) => {
              // First set the selected file for upload
              setSelectedFile(file);

              // Use the provided dataURL from FileReader
              if (previewUrl) {
                console.log("Setting preview from dataURL");
                setCurrentProfileImage(previewUrl);
              }
              // Fallback to object URL only if needed
              else if (file instanceof File) {
                console.log("Falling back to object URL");
                const objectUrl = URL.createObjectURL(file);
                setCurrentProfileImage(objectUrl);

                // Clean up the object URL when component unmounts
                return () => URL.revokeObjectURL(objectUrl);
              }

              // Call the parent handler
              onProfileImageChange(file);
            }}
            className="mb-6"
          />

          <div className="mt-4">
            <Input
              label="FULL NAME"
              icon={<WebIcon icon="Profile" size="small" />}
              placeholder="Enter your full name"
              value={isSearching ? "Loading..." : formData.fullname}
              onChange={(e) => handleChange(e, "fullname")}
            />
          </div>
        </div>

        <div className="mt-4">
          <h2 className="mb-2 text-bold">Contact Information</h2>
          <p className="mb-4 text-regular text-lighterGrey">
            So it will be easy to manage your account in case of password loss
            or recovery as well as for external usage to verify your identity.
          </p>
          <div className="mt-4">
            <Input
              label="PHONE NUMBER"
              icon={<CountryFlag />}
              placeholder="Enter your phone number"
              value={isSearching ? "Loading..." : formData.phone}
              onChange={(e) => handleChange(e, "phone")}
            />
          </div>
          <div className="mt-4">
            <Input
              label="EMAIL ADDRESS"
              icon={<WebIcon icon="Message" size="small" />}
              placeholder="Enter your email address"
              value={isSearching ? "Loading..." : formData.email}
              onChange={(e) => handleChange(e, "email")}
            />
          </div>
        </div>

        <Error
          isVisible={isErrorVisible}
          onClose={() => setIsErrorVisible(false)}
          header={errorMessage?.header || "Update Failed"}
          paragraph={
            errorMessage?.paragraph ||
            updateError ||
            "Failed to update user information. Please try again."
          }
          buttonText="Try Again"
        />

        <Success
          isVisible={isSuccessVisible}
          onClose={() => setIsSuccessVisible(false)}
          header="Update Successful"
          paragraph={
            shouldRedirectToSignin
              ? "Your email has been changed. Click Continue to sign in with your new email."
              : "Your information has been updated successfully."
          }
          buttonText={shouldRedirectToSignin ? "Continue" : "Done"}
          onButtonClick={() => {
            if (!shouldRedirectToSignin) return;
            // Route protection depends on auth state, so we must logout first
            // to prevent being kept on a protected dashboard.
            dispatch(logout());
            clearAccessToken();
            navigate("/signin");
          }}
        />
      </>
    );
  }
);

ManageAccount.displayName = "ManageAccount";

ManageAccount.propTypes = {
  profileImage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onProfileImageChange: PropTypes.func,
};

export default ManageAccount;
